'use client';

import { useEffect } from 'react';

import { useBehaviorSubjectUpdater } from '@ps/functions/hooks/useBehaviorSubject';
import { useCreation } from '@ps/functions/hooks/useCreation';
import {
  MediaFileUploadController,
  type MediaUploadTask,
} from '@ps/ui/upload/functions/MediaFileUploadController';
import type { BehaviorSubject } from 'rxjs';

import {
  type ChannelMessageMediaUploadResponse,
  channelMessageMediaFileService,
} from '../functions/message/channelMessageMediaFileService';
import type {
  ChannelMessageMediaInfo,
  LocalMediaUploadState,
} from '../types/ChannelMessageMediaInfo';
import { ChannelMessageMediaType } from '../types/ChannelMessageMediaInfo';

/**
 * Per-task context attached on `enqueue(...)`. The bridge uses it to
 * locate the `medias$` entry to patch on each event. Matches the
 * (clientMessageId, key) tuple PopshopRN uses on `medias$`.
 */
export interface ChannelMessageMediaUploadContext {
  clientMessageId: string;
  /** `medias$` entry id — used to dedupe / look up the in-flight row. */
  key: string;
}

export type ChannelMessageMediaUploader = MediaFileUploadController<
  ChannelMessageMediaUploadResponse,
  ChannelMessageMediaUploadContext
>;

/**
 * Build a `MediaFileUploadController` for channel-message uploads
 * AND wire it to keep `medias$` in sync with task events. Web port
 * of PopshopRN's `useCommunityChannelMessageMediaUploader`.
 *
 * The hook owns the controller for the lifetime of the calling
 * component (typically the community-channel provider). On every
 * upload event the bridge upserts / patches the matching `medias$`
 * entry so consumer bubbles see local previews + progress + final
 * URLs without re-fetching anything.
 *
 * Per-event mapping:
 *
 * - `queued` → upsert entry with `source: 'local'`, `state: queued`
 *   (carries the local preview URL + the `File` so retries can
 *   re-upload).
 * - `begin` → `state: uploading`, `progress: 0`.
 * - `progress` → `state: uploading`, `progress: <0…1>`.
 * - `error` / `abort` → `state: error` with the thrown error.
 *   The entry stays alive so the retry path can find it and
 *   re-enqueue.
 * - `finished` → swap `source` to `remote` with the server-side id
 *   + CDN URL. The provider clears the entry once the Firestore
 *   confirmation lands.
 */
export function useChannelMessageMediaUploader(
  creatorId: string | null | undefined,
  medias$: BehaviorSubject<ChannelMessageMediaInfo[]>,
): ChannelMessageMediaUploader | undefined {
  const update = useBehaviorSubjectUpdater(medias$);

  const uploader = useCreation<ChannelMessageMediaUploader | undefined>(() => {
    if (!creatorId) return undefined;
    return new MediaFileUploadController(
      channelMessageMediaFileService(creatorId),
      { concurrency: 3 },
    );
  }, [creatorId]);

  useEffect(() => {
    if (!uploader) return;
    const unsubscribe = uploader.addListener((event, task) => {
      const context = task.request.context;
      if (!context) return;
      const { clientMessageId, key } = context;
      const mediaType =
        task.request.type === 'image'
          ? ChannelMessageMediaType.Image
          : ChannelMessageMediaType.Video;

      switch (event.type) {
        case 'queued': {
          update(list =>
            upsertLocal(list, clientMessageId, key, mediaType, task),
          );
          return;
        }
        case 'begin': {
          update(list =>
            patchLocalState(list, key, { status: 'uploading', progress: 0 }),
          );
          return;
        }
        case 'progress': {
          update(list =>
            patchLocalState(list, key, {
              status: 'uploading',
              progress: event.progress,
            }),
          );
          return;
        }
        case 'error': {
          update(list =>
            patchLocalState(list, key, {
              status: 'error',
              error: event.error,
            }),
          );
          return;
        }
        case 'abort': {
          update(list =>
            patchLocalState(list, key, {
              status: 'error',
              error: event.error ?? new Error('Upload aborted'),
            }),
          );
          return;
        }
        case 'finished': {
          update(list =>
            list.map(entry =>
              entry.key === key
                ? {
                    ...entry,
                    source: {
                      type: 'remote',
                      contentType: event.result.contentType,
                      id: event.result.id,
                      url: event.result.url,
                      metadata: event.result.metadata,
                    },
                  }
                : entry,
            ),
          );
          return;
        }
      }
    });
    return () => {
      unsubscribe();
    };
  }, [uploader, update]);

  useEffect(() => {
    return () => {
      uploader?.dispose();
    };
  }, [uploader]);

  return uploader;
}

function upsertLocal(
  list: ChannelMessageMediaInfo[],
  clientMessageId: string,
  key: string,
  type: ChannelMessageMediaType,
  task: MediaUploadTask<
    ChannelMessageMediaUploadResponse,
    ChannelMessageMediaUploadContext
  >,
): ChannelMessageMediaInfo[] {
  const local = {
    type: 'local' as const,
    contentType: task.request.contentType,
    filename: task.request.filename,
    localFileUri: task.request.localFileUri,
    localFile: task.request.localFile,
    state: { status: 'queued' as const },
  };
  const index = list.findIndex(entry => entry.key === key);
  if (index >= 0) {
    const next = [...list];
    next[index] = { ...next[index], source: local };
    return next;
  }
  return [...list, { clientMessageId, key, type, source: local }];
}

function patchLocalState(
  list: ChannelMessageMediaInfo[],
  key: string,
  state: LocalMediaUploadState,
): ChannelMessageMediaInfo[] {
  return list.map(entry => {
    if (entry.key !== key) return entry;
    if (entry.source.type !== 'local') return entry;
    return { ...entry, source: { ...entry.source, state } };
  });
}
